import {
  API_ERROR_CODES,
  API_HEADERS,
  CONTENT_TYPE_JSON,
  CONTENT_TYPE_MULTIPART,
  type ApiResult,
} from "@bizy/core";
import { env } from "./env";

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: string, status: number, message: string, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  formData?: FormData;
  workspaceId?: string;
  signal?: AbortSignal;
}

const buildUrl = (path: string, workspaceId?: string): string => {
  const base = path.startsWith("/api/") ? path : `${env.apiBaseUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const url = new URL(base, env.appUrl || window.location.origin);
  return workspaceId ? `${url.toString()}${url.search ? "&" : "?"}workspace=${workspaceId}` : url.toString();
};

const isEnvelope = <T>(value: unknown): value is ApiResult<T> =>
  typeof value === "object" && value !== null && ("data" in value || "error" in value);

/**
 * Single entry point for the API.
 *
 * Preview build (`main`): no authentication header is sent — the Pages Function
 * resolves a fixed actor server-side (`PREVIEW_MODE`). Nothing in the bundle
 * carries a token or a Supabase key.
 */
export const apiFetch = async <T>(path: string, options: ApiFetchOptions = {}): Promise<T> => {
  const headers: Record<string, string> = { accept: CONTENT_TYPE_JSON };
  if (options.workspaceId) headers[API_HEADERS.workspaceId] = options.workspaceId;

  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["content-type"] = CONTENT_TYPE_JSON;
    body = JSON.stringify(options.body);
  }

  const response = await fetch(buildUrl(path, options.workspaceId), {
    method: options.method ?? "GET",
    headers,
    body,
    signal: options.signal,
  });

  const text = await response.text();
  const payload: unknown = text.length > 0 ? safeJson(text) : null;

  if (!response.ok) {
    const failure = isEnvelope<unknown>(payload) && "error" in payload ? payload.error : null;
    throw new ApiRequestError(
      failure?.code ?? API_ERROR_CODES.internalError,
      response.status,
      failure?.message ?? `Request failed with status ${response.status}.`,
      failure?.details,
    );
  }

  if (isEnvelope<T>(payload) && "data" in payload) return payload.data;
  return payload as T;
};

const safeJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export const apiGet = <T>(path: string, options?: Omit<ApiFetchOptions, "method" | "body">) =>
  apiFetch<T>(path, { ...options, method: "GET" });

export const apiPost = <T>(path: string, body?: unknown, options?: Omit<ApiFetchOptions, "method" | "body">) =>
  apiFetch<T>(path, { ...options, method: "POST", body });

export const apiPatch = <T>(path: string, body?: unknown, options?: Omit<ApiFetchOptions, "method" | "body">) =>
  apiFetch<T>(path, { ...options, method: "PATCH", body });

export const apiDelete = <T>(path: string, options?: Omit<ApiFetchOptions, "method" | "body">) =>
  apiFetch<T>(path, { ...options, method: "DELETE" });

export const apiUpload = <T>(path: string, formData: FormData, options?: Omit<ApiFetchOptions, "method" | "body" | "formData">) =>
  apiFetch<T>(path, { ...options, method: "POST", formData });

export const isMultipart = (value: string): boolean => value.includes(CONTENT_TYPE_MULTIPART);
